/**
 * Deterministic game clock. 1 tick = 1 in-game hour. 1x = one game hour per 90 real
 * second. Speeds 0/1/2/3/5 (0 = paused). Pure: real time never enters here;
 * callers pass elapsed milliseconds in, ticks come out.
 */
import type { ClockState, Speed, Tick } from '../../contracts/sim';

/** Pacing (Matthew 2026-10-22, via main): at standard 1x speed one game hour
 *  takes 90 real seconds ("each hour should be 1.5 minutes; the days cycle
 *  too fast - we can speed up with the multiplier buttons"). A 1x game day is
 *  36 real minutes; 2x/3x/5x divide that. */
export const MS_PER_TICK_1X = 90_000;

/** Floor for non-negative values in int32 range (Math is lint-banned in src/sim). */
function floorU32(x: number): number {
  return x | 0; // truncation === floor for x >= 0
}
export const DAY_HOURS = 24;
export const SEASON_DAYS = 90;
export const YEAR_DAYS = 360;

export class Clock {
  private tickValue: Tick = 0;
  private speedValue: Speed = 1;
  /** Sub-tick accumulator, integer numerator over MS_PER_TICK_1X ms. Not sim state; not hashed. */
  private carryNum = 0;
  private carryRate = 0; // the rate (ticks per 90s = speed at 1x) the numerator was accrued at

  get tick(): Tick { return this.tickValue; }
  get speed(): Speed { return this.speedValue; }

  setSpeed(speed: Speed): void {
    if (![0, 1, 2, 3, 5].includes(speed)) throw new Error(`clock: invalid speed ${String(speed)}`);
    this.speedValue = speed;
  }

  /**
   * Advance by real elapsed time. Returns whole ticks to simulate now.
   * Fractional remainders carry over as an integer numerator (no float
   * drift): rate = speed ticks per MS_PER_TICK_1X ms (1x = 90s per game
   * hour). Paused (speed 0) returns 0.
   */
  advance(elapsedMs: number): number {
    if (elapsedMs < 0) throw new Error('clock: negative elapsed');
    if (this.speedValue === 0) return 0;
    const rate = this.speedValue; // ticks per MS_PER_TICK_1X ms
    if (rate !== this.carryRate) {
      // Re-base the carried fraction onto the new rate proportionally.
      this.carryNum = this.carryRate === 0 ? 0 : floorU32((this.carryNum * rate) / this.carryRate);
      this.carryRate = rate;
    }
    const total = this.carryNum + floorU32(elapsedMs) * rate;
    const ticks = floorU32(total / MS_PER_TICK_1X);
    this.carryNum = total - ticks * MS_PER_TICK_1X;
    return ticks;
  }

  /** Direct tick advance (headless runs, tests, worker fixed-step). */
  stepTicks(n: number): void {
    if (!Number.isInteger(n) || n < 0) throw new Error('clock: bad step');
    this.tickValue += n;
  }

  getState(): ClockState {
    return {
      tick: this.tickValue,
      speed: this.speedValue,
      dayLengthHours: DAY_HOURS,
      seasonLengthDays: SEASON_DAYS,
      yearLengthDays: YEAR_DAYS,
    };
  }

  restore(tick: Tick, speed: Speed): void {
    this.tickValue = tick;
    this.setSpeed(speed);
    this.carryNum = 0;
    this.carryRate = 0;
  }
}

/** Calendar math over the tick counter. All integer math. */
export function calendarOf(tick: Tick): { hour: number; day: number; season: number; year: number; dayOfSeason: number; dayOfYear: number } {
  const hour = tick % DAY_HOURS;
  const day = floorU32(tick / DAY_HOURS);
  const year = floorU32(day / YEAR_DAYS);
  const dayOfYear = day % YEAR_DAYS;
  const season = floorU32(dayOfYear / SEASON_DAYS);
  const dayOfSeason = dayOfYear % SEASON_DAYS;
  return { hour, day, season, year, dayOfSeason, dayOfYear };
}
